import { useMediaDevices, usePreviewTracks, useTrackVolume } from '@livekit/components-react';
import { Track, type LocalAudioTrack, type LocalVideoTrack } from 'livekit-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStrings } from '../i18n/context';

export interface DeviceChoices {
  readonly cameraOn: boolean;
  readonly microphoneOn: boolean;
  /** Empty = the browser's default device. */
  readonly cameraId: string;
  readonly microphoneId: string;
}

interface Props {
  readonly value: DeviceChoices;
  readonly onChange: (next: DeviceChoices) => void;
}

interface DeviceSelectProps {
  readonly id: string;
  readonly label: string;
  readonly devices: readonly MediaDeviceInfo[];
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (deviceId: string) => void;
}

function DeviceSelect({ id, label, devices, value, disabled, onChange }: DeviceSelectProps) {
  const strings = useStrings();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value="">{strings.defaultDevice}</option>
        {devices
          .filter((device) => device.deviceId && device.deviceId !== 'default')
          .map((device, index) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label || `${label} ${index + 1}`}
            </option>
          ))}
      </select>
    </div>
  );
}

/**
 * The lobby's device check: a camera preview, a microphone level meter and the device choice.
 * The preview tracks stop when the lobby closes; the room opens its own with the same choices.
 */
export function DeviceCheck({ value, onChange }: Props) {
  const strings = useStrings();
  const [failed, setFailed] = useState(false);
  const onError = useCallback(() => setFailed(true), []);

  // usePreviewTracks compares the options by value, so a new object with the same
  // content does not reopen the devices.
  const options = useMemo(
    () => ({
      audio: value.microphoneOn ? (value.microphoneId ? { deviceId: value.microphoneId } : true) : false,
      video: value.cameraOn ? (value.cameraId ? { deviceId: value.cameraId } : true) : false,
    }),
    [value.cameraOn, value.cameraId, value.microphoneOn, value.microphoneId],
  );
  const tracks = usePreviewTracks(options, onError);
  const videoTrack = value.cameraOn
    ? (tracks?.find((track) => track.kind === Track.Kind.Video) as LocalVideoTrack | undefined)
    : undefined;
  const audioTrack = value.microphoneOn
    ? (tracks?.find((track) => track.kind === Track.Kind.Audio) as LocalAudioTrack | undefined)
    : undefined;
  const level = useTrackVolume(audioTrack);

  const videoElement = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = videoElement.current;
    if (!element || !videoTrack) return undefined;
    videoTrack.attach(element);
    return () => {
      videoTrack.detach(element);
    };
  }, [videoTrack]);

  const cameras = useMediaDevices({ kind: 'videoinput' });
  const microphones = useMediaDevices({ kind: 'audioinput' });

  return (
    <fieldset className="devices">
      <legend>{strings.deviceHeading}</legend>
      <div className="devices__preview">
        {videoTrack ? (
          <video ref={videoElement} className="devices__video" muted playsInline aria-label={strings.previewLabel} />
        ) : (
          <div className="devices__video devices__video--off">{strings.previewOff}</div>
        )}
      </div>

      <label className="check">
        <input
          type="checkbox"
          checked={value.cameraOn}
          onChange={(event) => onChange({ ...value, cameraOn: event.target.checked })}
        />
        <span>{strings.cameraStartsOn}</span>
      </label>
      <DeviceSelect
        id="vm-camera"
        label={strings.cameraDevice}
        devices={cameras}
        value={value.cameraId}
        disabled={!value.cameraOn}
        onChange={(cameraId) => onChange({ ...value, cameraId })}
      />

      <label className="check">
        <input
          type="checkbox"
          checked={value.microphoneOn}
          onChange={(event) => onChange({ ...value, microphoneOn: event.target.checked })}
        />
        <span>{strings.microphoneStartsOn}</span>
      </label>
      <DeviceSelect
        id="vm-microphone"
        label={strings.microphoneDevice}
        devices={microphones}
        value={value.microphoneId}
        disabled={!value.microphoneOn}
        onChange={(microphoneId) => onChange({ ...value, microphoneId })}
      />
      <div className="meter">
        <span id="vm-level-label">{strings.microphoneLevel}</span>
        <meter aria-labelledby="vm-level-label" min={0} max={1} value={audioTrack ? level : 0} />
      </div>

      {failed && (
        <p role="alert" className="notice notice--error">
          {strings.devicesError}
        </p>
      )}
      <p className="field__hint">{strings.headphonesTip}</p>
    </fieldset>
  );
}
